/**
 * 练习报告: how an ended practice went and what to practise next. The
 * first open writes the closing pass (正在复盘).
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { CoachingNavParams } from '@euler/app/flows/coaching';
import { EmptyView } from '@euler/components/EmptyView';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { CoachingAttemptView } from '@euler/model/coaching';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useRef } from 'react';
import { View } from 'react-native';
import { aiColors } from '../../../ai-suite/theme';
import { SubtitledHeader } from '../../components/SubtitledHeader';
import { formatName, levelName } from '../../copy';
import { openCoachingCase } from '../../functions/openCoachingSetup';
import { usePracticeAgain } from '../../practice/functions/usePracticeAgain';
import { usePracticeReport } from '../../practice/functions/usePracticeReport';
import { ReportView } from '../../practice/report/ReportView';
import { ReviewingSteps } from '../../practice/report/components/ReviewingSteps';

export const CoachingReportScreen = wrapNavigatorScreen(
  ({ attemptId }: { attemptId: string }) => {
    const navigation = useNavigation<StackNavigationProp<CoachingNavParams>>();
    const appNavigation = useAppNavigation();
    const { state, reload } = usePracticeReport(attemptId);
    // Back from a moment replay: its mark on the moment card.
    const focused = useRef(false);
    useFocusEffect(
      useCallback(() => {
        if (focused.current) reload();
        focused.current = true;
      }, [reload]),
    );
    const again = usePracticeAgain(
      attemptId,
      useCallback(
        (attempt: CoachingAttemptView) =>
          navigation.replace('practice', { attemptId: attempt.id }),
        [navigation],
      ),
    );
    const report = state.kind === 'ready' ? state.report : null;
    return (
      <View style={{ flex: 1, backgroundColor: aiColors.background }}>
        <SubtitledHeader
          title="练习报告"
          subtitle={
            report
              ? `${report.title} · ${formatName(report.format)} · ${levelName(
                  report.difficulty,
                )}`
              : undefined
          }
          onBack={() => navigation.goBack()}
        />
        {state.kind === 'reviewing' ? (
          <ReviewingSteps />
        ) : state.kind === 'failed' ? (
          <EmptyView message="复盘没生成出来" onRetry={reload} />
        ) : (
          <ReportView
            report={state.report}
            replays={state.replays}
            actions={{
              onRetryMoment: turn =>
                navigation.push('practice', { attemptId, momentTurn: turn }),
              onAgain: levelUp => void again.start(levelUp),
              againBusy: again.busy,
              againFailed: again.failed,
              onPractise: card =>
                openCoachingCase(appNavigation, {
                  caseId: card.id,
                  title: card.title,
                }),
              onOpenTranscript: () =>
                navigation.push('transcript', { attemptId }),
            }}
          />
        )}
      </View>
    );
  },
  { title: '练习报告', headerShown: false },
);
